import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

const FacebookIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

function Spinner() {
  return <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />;
}

export default function MetaConnectButton({ onConectado }) {
  const [status, setStatus] = useState(null);
  const [etapa, setEtapa] = useState('idle'); // idle | escolhendo_pagina | conectado
  const [paginas, setPaginas] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [carregandoPaginas, setCarregandoPaginas] = useState(false);

  const carregarStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/meta/status');
      const s = data.data;
      setStatus(s);
      if (s?.conectado) setEtapa('conectado');
    } catch {
      setStatus({ conectado: false, status: 'desconectado' });
    }
  }, []);

  const carregarPaginas = useCallback(async () => {
    setCarregandoPaginas(true);
    try {
      const { data } = await api.get('/meta/paginas');
      if (data.success) setPaginas(data.data || []);
      else alert('Erro ao carregar páginas: ' + (data.error || 'Tente novamente.'));
    } catch {
      alert('Erro ao carregar páginas do Facebook.');
    } finally {
      setCarregandoPaginas(false);
    }
  }, []);

  useEffect(() => {
    carregarStatus();

    // Verificar parâmetros de retorno do OAuth
    const params = new URLSearchParams(window.location.search);
    const metaParam = params.get('meta');
    if (metaParam === 'escolher_pagina') {
      setEtapa('escolhendo_pagina');
      carregarPaginas();
      window.history.replaceState({}, '', window.location.pathname);
    } else if (metaParam === 'erro') {
      const msg = params.get('msg') || 'Erro ao conectar com o Facebook.';
      // Usar timeout para garantir que o DOM já renderizou
      setTimeout(() => alert(`Erro ao conectar com o Facebook:\n${msg}`), 100);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [carregarStatus, carregarPaginas]);

  async function iniciarOAuth() {
    setCarregando(true);
    try {
      const { data } = await api.get('/meta/auth-url');
      if (data.success) {
        window.location.href = data.url;
        // não setar carregando=false pois a página vai redirecionar
      } else {
        alert('Erro: ' + (data.error || 'Não foi possível gerar URL de autorização.'));
        setCarregando(false);
      }
    } catch {
      alert('Erro ao conectar. Verifique se META_APP_ID está configurado no servidor.');
      setCarregando(false);
    }
  }

  async function selecionarPagina(pagina) {
    setCarregando(true);
    try {
      const { data } = await api.post('/meta/selecionar-pagina', {
        page_id: pagina.id,
        page_name: pagina.name,
        page_access_token: pagina.access_token,
        page_category: pagina.category,
        page_picture_url: pagina.picture,
      });
      if (data.success) {
        await carregarStatus();
        setEtapa('conectado');
        if (onConectado) onConectado(data.data);
        if (data.data?.aviso) alert(`⚠️ ${data.data.aviso}`);
      } else {
        alert('Erro ao conectar página: ' + (data.error || 'Tente novamente.'));
      }
    } catch {
      alert('Erro ao selecionar página.');
    } finally {
      setCarregando(false);
    }
  }

  async function desconectar() {
    if (!window.confirm('Deseja desconectar o Facebook?\nOs leads já capturados não serão perdidos.')) return;
    setCarregando(true);
    try {
      await api.delete('/meta/desconectar');
      setStatus({ conectado: false, status: 'desconectado' });
      setEtapa('idle');
      setPaginas([]);
    } catch {
      alert('Erro ao desconectar.');
    } finally {
      setCarregando(false);
    }
  }

  // ─── Carregando status inicial ────────────────────────────────────────────
  if (status === null) {
    return (
      <div className="flex items-center gap-2 text-gray-400 text-sm py-2">
        <div className="w-4 h-4 border-2 border-gray-200 border-t-gray-400 rounded-full animate-spin" />
        Verificando conexão...
      </div>
    );
  }

  // ─── Escolhendo página ────────────────────────────────────────────────────
  if (etapa === 'escolhendo_pagina') {
    return (
      <div className="space-y-3">
        <p className="text-sm font-medium text-gray-700">
          Escolha a página para capturar leads:
        </p>

        {carregandoPaginas ? (
          <div className="flex items-center gap-2 text-gray-400 text-sm py-2">
            <div className="w-4 h-4 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin" />
            Carregando páginas...
          </div>
        ) : paginas.length === 0 ? (
          <p className="text-sm text-amber-600 bg-amber-50 rounded-lg p-3">
            Nenhuma página encontrada. Certifique-se de ser administrador de uma Página do Facebook.
          </p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
            {paginas.map(pagina => (
              <button
                key={pagina.id}
                onClick={() => selecionarPagina(pagina)}
                disabled={carregando}
                className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all text-left disabled:opacity-50"
              >
                {pagina.picture ? (
                  <img src={pagina.picture} alt={pagina.name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[#1877F2] flex items-center justify-center text-white flex-shrink-0">
                    <FacebookIcon />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{pagina.name}</p>
                  <p className="text-xs text-gray-400">{pagina.category}</p>
                  {!pagina.leadgen_habilitado && (
                    <p className="text-xs text-amber-600 mt-0.5">⚠️ Termos de Lead Ads não aceitos</p>
                  )}
                </div>
                {carregando ? (
                  <div className="w-4 h-4 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin flex-shrink-0" />
                ) : (
                  <span className="text-blue-500 text-sm flex-shrink-0">Selecionar →</span>
                )}
              </button>
            ))}
          </div>
        )}

        <button
          onClick={() => { setEtapa('idle'); setPaginas([]); }}
          className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
        >
          ← Cancelar
        </button>
      </div>
    );
  }

  // ─── Conectado ────────────────────────────────────────────────────────────
  if (etapa === 'conectado' && status?.conectado) {
    const formularios = Array.isArray(status.formularios) ? status.formularios : [];
    return (
      <div className="space-y-3">
        {/* Página conectada */}
        <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50 border border-green-200">
          {status.pagina?.foto ? (
            <img src={status.pagina.foto} alt={status.pagina.nome} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-[#1877F2] flex items-center justify-center text-white flex-shrink-0">
              <FacebookIcon />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-gray-900 truncate">{status.pagina?.nome}</span>
              <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                Conectado
              </span>
            </div>
            <p className="text-xs text-gray-500 truncate">Por: {status.usuario?.nome}</p>
          </div>
        </div>

        {/* Status webhook */}
        <div className={`flex items-start gap-2 text-xs px-3 py-2 rounded-lg border ${
          status.webhook_ativo
            ? 'bg-blue-50 text-blue-700 border-blue-200'
            : 'bg-amber-50 text-amber-700 border-amber-200'
        }`}>
          <span className="flex-shrink-0 mt-0.5">{status.webhook_ativo ? '✅' : '⚠️'}</span>
          <span>
            {status.webhook_ativo
              ? 'Webhook ativo — leads chegam automaticamente'
              : 'Webhook não ativo. Verifique META_WEBHOOK_URL no servidor.'}
          </span>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-xl font-bold text-gray-900">{status.total_leads || 0}</p>
            <p className="text-xs text-gray-500 mt-0.5">Leads recebidos</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-xl font-bold text-gray-900">{formularios.filter(f => f.ativo !== false).length}</p>
            <p className="text-xs text-gray-500 mt-0.5">Formulários ativos</p>
          </div>
        </div>

        {/* Formulários */}
        {formularios.length > 0 && (
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1.5">Formulários de Lead Ads:</p>
            <div className="space-y-1 max-h-28 overflow-y-auto">
              {formularios.map(f => (
                <div key={f.id} className="flex items-center justify-between text-xs bg-white border border-gray-100 rounded px-2.5 py-1.5">
                  <span className="text-gray-700 truncate mr-2">{f.name}</span>
                  <span className={`flex-shrink-0 font-medium ${f.status === 'ACTIVE' ? 'text-green-600' : 'text-gray-400'}`}>
                    {f.status === 'ACTIVE' ? '● Ativo' : '○ Inativo'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Ações */}
        <div className="flex gap-2 pt-1">
          <button
            onClick={desconectar}
            disabled={carregando}
            className="flex-1 text-xs py-2 px-3 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
          >
            {carregando ? '...' : 'Desconectar'}
          </button>
          <button
            onClick={carregarStatus}
            disabled={carregando}
            className="flex-1 text-xs py-2 px-3 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            ↻ Atualizar
          </button>
        </div>
      </div>
    );
  }

  // ─── Erro / token expirado ────────────────────────────────────────────────
  if (status?.status === 'token_expirado' || status?.status === 'erro') {
    return (
      <div className="space-y-3">
        <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
          <span className="flex-shrink-0">⚠️</span>
          <span>
            {status.status === 'token_expirado'
              ? 'Sua autorização expirou. Reconecte para continuar recebendo leads.'
              : `Erro de conexão: ${status.ultimo_erro || 'Tente reconectar.'}`}
          </span>
        </div>
        <button
          onClick={iniciarOAuth}
          disabled={carregando}
          className="w-full flex items-center justify-center gap-2 bg-[#1877F2] hover:bg-[#166FE5] text-white font-semibold py-2.5 px-4 rounded-lg transition-colors disabled:opacity-70 shadow-sm"
        >
          {carregando ? <Spinner /> : <FacebookIcon />}
          {carregando ? 'Redirecionando...' : 'Reconectar com Facebook'}
        </button>
      </div>
    );
  }

  // ─── Desconectado (padrão) ────────────────────────────────────────────────
  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500 leading-relaxed">
        Conecte sua Página do Facebook para receber leads dos anúncios automaticamente no CRM.
      </p>
      <button
        onClick={iniciarOAuth}
        disabled={carregando}
        className="w-full flex items-center justify-center gap-2.5 bg-[#1877F2] hover:bg-[#166FE5] active:bg-[#1569D3] text-white font-semibold py-2.5 px-4 rounded-lg transition-colors disabled:opacity-70 shadow-sm"
      >
        {carregando ? <Spinner /> : <FacebookIcon />}
        {carregando ? 'Redirecionando para o Facebook...' : 'Continuar com Facebook'}
      </button>
      <p className="text-xs text-gray-400 text-center">
        Você será redirecionado para autorizar o acesso.
      </p>
    </div>
  );
}
